import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { fetchDashboardMeta, getCachedDashboardMeta, getSavedDashboardMeta, SAVED_PROBE_FALLBACK_MS, type DashboardMeta, type DashboardTarget } from '../../lib/dashboard';
import { isWorkspaceSlug } from '../../lib/pages';
import { useBasePath } from '../../lib/use-base-path';
import { useDashboardDuplexer } from '../../lib/use-dashboard-duplexer';
import { useRevalidateOnReveal } from '../../lib/use-revalidate-on-reveal';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useLoadBi } from '../../lib/use-load-bi';
import {
  DASHBOARD_RENDER_COMPONENT,
  dashboardEntity,
  dashboardOutcomeFields,
  dashboardPageFields,
} from '../../lib/dashboard-render-bi';
import { useHomepage } from '../HomepageContext';
import { DashboardFrame, DashboardSkeleton } from './DashboardView';
import { useTranslation } from '../../lib/i18n';
import { useNarrow } from '../../lib/use-narrow';

const PAGE = 'dashboard';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// This tab's last probe, else what the last page load's probe found (`saved`: painted at once, still to be confirmed
// by this load's probe). undefined = probing with nothing to show.
function initialMeta(target: DashboardTarget): { meta: DashboardMeta | null | undefined; saved: boolean } {
  const cached = getCachedDashboardMeta(target);
  if (cached !== undefined) return { meta: cached, saved: false };
  const saved = getSavedDashboardMeta(target);
  return { meta: saved, saved: saved !== undefined };
}

// Renders ONE Orion surface — a dashboard bento (a bento of widgets) or a drill-in page (a slot-less detail
// layout) — through one pipeline: a render-handler existence probe resolves
// whether the dashboard/page exists (+ its name, via the document's own status/headers), and DashboardFrame
// points a sandboxed iframe's `src` at the DashboardRenderHandler for the finished document. So the workspace
// dashboard and every drill-in render identically to the home.
//
//  - `workspace` is the owning dashboard's slug: given → render that workspace's dashboard; omitted →
//    the home/catch-up dashboard. It also scopes a widget's onward drill-in (…/workspace/<ws>/pages/<page>).
//  - `onBack` shows a subtle in-app back affordance (drill-in pages pass it); browser back works regardless.
//  - `navParams` is a drill-in's params, the route's raw `?params=` value; the page reads them with
//    `sym.params()` (lib/nav-params). Passed by the caller, not read from the router here, so a hidden
//    keep-alive instance keeps answering with ITS params rather than whatever page is showing now.
export function DashboardScreen({
  page,
  workspace,
  navParams,
  onBack,
  missing,
  header,
}: {
  page?: string;
  workspace?: string;
  navParams?: string;
  onBack?: () => void;
  missing?: ReactNode;
  // Optional chrome pinned to the top of the content area (the workspace tab bar). Rendered in every
  // branch so it's always present; passed with an explicit `active` by the caller (not derived from the
  // router) so hidden keep-alive instances of other workspaces still highlight the right tab. The
  // missing branch renders it via the caller-supplied `missing` node (e.g. ComingSoon's own `header`).
  header?: ReactNode;
}) {
  const target = { dashboard: workspace, page };
  // Seed from this target's module cache so re-entry paints instantly; undefined = probing, null =
  // resolved-but-empty (no such dashboard/page). The saved probe of the last page load is applied by the layout
  // effect below, not here: the server has no storage, so reading it in the first render would not hydrate.
  const [meta, setMeta] = useState<DashboardMeta | null | undefined>(() => getCachedDashboardMeta(target));
  // Painted from the saved probe and not yet confirmed by this load's. The load BI stays open until it is, so a reload
  // still reports one load with its real outcome, as it did when it waited on the probe.
  const [confirming, setConfirming] = useState(false);
  const [docHeight, setDocHeight] = useState<number | null>(null);
  // Bumped on a live edit so the iframe refetches (an unchanged document still 304s via its ETag).
  const [reloadKey, setReloadKey] = useState(0);
  // The probe failed with nothing to show (the reader is left on the loader). Only the BI reads it: the
  // render keeps the loader, and the next dashboard-changed signal or a refresh retries.
  const [probeFailed, setProbeFailed] = useState(false);

  // src:198 load — this surface's `fetchDashboardMeta({dashboard,page})` probe. `meta`
  // is undefined while probing (a cache hit seeds it defined on mount → cached render
  // emits nothing). The finish carries the render outcome (ready / empty / error) and which
  // dashboard it was — the contract in lib/dashboard-render-bi, shared with both phones.
  const biFields = dashboardPageFields(workspace, page);
  useLoadBi(
    DASHBOARD_RENDER_COMPONENT,
    { isLoading: (meta === undefined || confirming) && !probeFailed, isError: probeFailed },
    {
      entity: dashboardEntity(biFields),
      additionalFields: dashboardOutcomeFields(biFields, probeFailed ? 'error' : meta ? 'ready' : 'empty'),
    },
  );

  const load = useCallback(
    (force?: boolean) => {
      let cancelled = false;
      fetchDashboardMeta({ dashboard: workspace, page }, { force })
        .then((m) => {
          if (!cancelled) {
            setMeta(m);
            setProbeFailed(false);
            setConfirming(false);
          }
        })
        .catch(() => {
          // Transient failure — keep the current render; the next dashboard-changed signal or a refresh retries.
          if (!cancelled) {
            setProbeFailed(true);
            setConfirming(false);
          }
        });
      return () => {
        cancelled = true;
      };
    },
    [workspace, page],
  );

  // The probe is a full GET of the same document the iframe loads. Run together, the two requests render it twice at
  // once and the iframe arrives later than it would alone, so a saved paint lets the iframe load first and confirms
  // after it (or after SAVED_PROBE_FALLBACK_MS).
  const cancelProbeRef = useRef<(() => void) | null>(null);
  const probePendingRef = useRef(false);
  const startProbe = useCallback(() => {
    probePendingRef.current = false;
    cancelProbeRef.current?.();
    cancelProbeRef.current = load();
  }, [load]);
  const onFrameReady = useCallback(() => {
    if (probePendingRef.current) startProbe();
  }, [startProbe]);

  // These routes share a pathname across slugs (/pages/[slug], /workspace/[slug]/pages/[page]), so this
  // component isn't remounted between them — re-seed from the new target's cache and re-probe on change. A layout
  // effect, so a saved probe is on the first painted frame after hydration and the iframe starts loading at once.
  useIsomorphicLayoutEffect(() => {
    const next = initialMeta({ dashboard: workspace, page });
    setMeta(next.meta);
    setConfirming(next.saved);
    setProbeFailed(false);
    setDocHeight(null);
    let fallback: ReturnType<typeof setTimeout> | undefined;
    if (next.saved) {
      probePendingRef.current = true;
      fallback = setTimeout(() => {
        if (probePendingRef.current) startProbe();
      }, SAVED_PROBE_FALLBACK_MS);
    } else {
      startProbe();
    }
    return () => {
      clearTimeout(fallback);
      probePendingRef.current = false;
      cancelProbeRef.current?.();
      cancelProbeRef.current = null;
    };
  }, [workspace, page, startProbe]);

  // Live update: when Orion rewrites this dashboard/page (a DashboardService write), the dashboard-changed
  // signal fires — re-probe and bump the iframe cache-buster so it re-renders.
  useDashboardDuplexer(() => {
    load(true);
    setReloadKey((k) => k + 1);
  });
  // A component-library publish writes none of THIS site's dashboard rows, so no
  // per-site signal fires and a mounted surface would keep its old composition until a full page load.
  useRevalidateOnReveal(() => setReloadKey((k) => k + 1));

  const router = useRouter();
  const basePath = useBasePath();
  const { state: homepage } = useHomepage();

  // Recovery: a GLOBAL drill-in page (no workspace) that resolves empty but names a known workspace is a
  // mis-addressed workspace nav that dead-ended as a `{ page }`. Send the user to the workspace's dashboard
  // rather than a dead-end "unavailable". Guarded to the ACTIVE `/pages/[slug]` route so a keep-alive
  // (hidden) instance of this screen never fires a redirect. Web-side net only — `{ workspace }` is an
  // orion-web-local destination (native + common-lib's `NavDestination` don't have it yet).
  const missingPageIsWorkspace =
    meta === null && !workspace && !!page && homepage.status === 'present' && isWorkspaceSlug(homepage.index, page);
  const isActivePageRoute = router.pathname === '/pages/[slug]' && router.query.slug === page;
  useEffect(() => {
    if (!missingPageIsWorkspace || !isActivePageRoute || !page) {
      return;
    }
    // eslint-disable-next-line no-console
    console.info('[dashboard-nav] page not found but matches a workspace — redirecting to its dashboard', { slug: page });
    void router.replace(
      { pathname: '/workspace/[slug]', query: { slug: page } },
      `${basePath}/workspace/${encodeURIComponent(page)}`,
    );
  }, [missingPageIsWorkspace, isActivePageRoute, page, basePath, router]);

  if (meta) {
    return (
      <Pane scroll={docHeight != null} onBack={onBack} header={header}>
        <DashboardFrame
          target={{ dashboard: workspace, page }}
          name={meta.name}
          workspace={workspace}
          navParams={navParams}
          reloadKey={reloadKey}
          docHeight={docHeight}
          onHeight={setDocHeight}
          onReady={onFrameReady}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </Pane>
    );
  }
  if (meta === null) {
    // Redirecting a mis-addressed workspace nav (see above) — hold the loader instead of flashing "unavailable".
    if (missingPageIsWorkspace && isActivePageRoute) {
      return <LoadingPane header={header} />;
    }
    // A caller-supplied empty state (e.g. a workspace dashboard's "coming soon") owns its own pane and is a
    // top-level surface with no back affordance. It carries the top chrome itself (WorkspaceDashboard passes
    // the same tab bar into ComingSoon's `header`), so it renders as-is. The default "not available" is a
    // drill-in dead end, so it carries the same back button as the resolved page — otherwise the user is
    // stranded with no way back.
    if (missing) {
      return <>{missing}</>;
    }
    return (
      <Pane onBack={onBack} header={header}>
        <PageUnavailableMessage />
      </Pane>
    );
  }
  return <LoadingPane header={header} />;
}

// The content pane the page fills — the same absolute `.orion-content-pane` the home uses (which
// DashboardFrame's viewport logic keys on). In `scroll` mode the whole page scrolls around a content-sized
// iframe; otherwise the pane is fixed and the iframe fills it (pre-height fallback). An optional subtle
// back affordance sits above the content.
function Pane({
  children,
  scroll = false,
  onBack,
  header,
}: {
  children: ReactNode;
  scroll?: boolean;
  onBack?: () => void;
  header?: ReactNode;
}) {
  // Match AppShell's mobile top padding (64px band + 32px gap to the header, Figma 4176-6999 /
  // 4944-5613); desktop keeps 72px. Without this the workspace hero sits ~24px too high on mobile.
  const narrow = useNarrow();
  return (
    <section
      className="orion-content-pane"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        zIndex: 4,
        overflowY: scroll ? 'auto' : 'hidden',
        overflowX: 'hidden',
        paddingBlock: narrow ? '96px 40px' : '72px 40px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {header}
      {/* Desktop back affordance; on mobile the header back button covers it. */}
      {onBack && !narrow && <BackButton onClick={onBack} />}
      {scroll ? (
        <div style={{ position: 'relative', width: '100%', flexShrink: 0 }}>{children}</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>{children}</div>
      )}
    </section>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <button
      type="button"
      onClick={() => {
        logPageClickBi({ page_name: PAGE, button_name: 'back' });
        onClick();
      }}
      style={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        margin: '0 0 14px',
        paddingBlock: 6,
        paddingInlineEnd: 12,
        paddingInlineStart: 8,
        border: 'none',
        borderRadius: 999,
        background: 'transparent',
        color: 'rgba(var(--ink-rgb),0.55)',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 'var(--sym-weight-medium)',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(var(--ink-rgb),0.05)';
        e.currentTarget.style.color = 'rgba(var(--ink-rgb),0.85)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'rgba(var(--ink-rgb),0.55)';
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden className="dir-flip">
        <path d="M9.5 3.5L5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t('common.back')}
    </button>
  );
}

function PageUnavailableMessage() {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ maxWidth: 420, textAlign: 'center', fontSize: 15, lineHeight: 1.6, color: 'rgba(var(--ink-rgb),0.5)' }}>
        {t('dashboard.pageUnavailable')}
      </p>
    </div>
  );
}

function LoadingPane({ header }: { header?: ReactNode }) {
  const narrow = useNarrow();
  return (
    <section
      className="orion-content-pane"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        zIndex: 4,
        overflow: 'hidden',
        paddingBlock: narrow ? '96px 40px' : '72px 40px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {header}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <DashboardSkeleton />
      </div>
    </section>
  );
}
